import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminService, User, CreateClinicRequest, CreateAdminRequest } from '../services/adminService';
import './AdminPanel.css';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateClinic, setShowCreateClinic] = useState(false);
  const [clinicForm, setClinicForm] = useState<CreateClinicRequest>({
    cnpj: '',
    email: '',
    name: '',
  });
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [createdClinic, setCreatedClinic] = useState<User | null>(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [adminForm, setAdminForm] = useState<CreateAdminRequest>({
    email: '',
    name: '',
    password: '',
  });
  const [adminCreated, setAdminCreated] = useState<User | null>(null);

  useEffect(() => {
    // Verificar se é admin ou super_admin
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/admin/login', { replace: true });
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.listUsers();
      setUsers(response.users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClinic = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Limpar CNPJ (remover formatação)
    const cnpjClean = clinicForm.cnpj.replace(/\D/g, '');
    
    if (cnpjClean.length !== 14) {
      setError('CNPJ deve conter 14 dígitos');
      return;
    }

    try {
      const response = await adminService.createClinic({
        ...clinicForm,
        cnpj: cnpjClean,
      });
      setCreatedPassword(response.password);
      setCreatedClinic(response.user);
      setClinicForm({ cnpj: '', email: '', name: '' });
      setShowCreateClinic(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar clínica');
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (adminForm.password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }
    try {
      const response = await adminService.createAdmin(adminForm);
      setAdminCreated(response.user);
      setAdminForm({ email: '', name: '', password: '' });
      setShowCreateAdmin(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar administrador');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este usuário?')) {
      return;
    }

    try {
      await adminService.deleteUser(userId);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao deletar usuário');
    }
  };

  const formatCNPJ = (cnpj: string) => {
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  };

  if (loading && users.length === 0) {
    return (
      <div className="admin-panel-container">
        <div className="admin-panel-loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="admin-panel-container">
      <div className="admin-panel-header">
        <h1>Painel Administrativo</h1>
        <div className="admin-panel-actions">
          {user?.role === 'super_admin' && (
            <button onClick={() => setShowCreateAdmin(true)} className="btn btn-primary">
              Criar Administrador
            </button>
          )}
          <button onClick={() => setShowCreateClinic(true)} className="btn btn-primary">
            Criar Clínica
          </button>
          <button onClick={logout} className="btn btn-secondary">
            Sair
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {createdPassword && createdClinic && (
        <div className="success-message">
          <h3>Clínica criada com sucesso!</h3>
          <p><strong>Nome:</strong> {createdClinic.name}</p>
          <p><strong>Email:</strong> {createdClinic.email}</p>
          <p><strong>CNPJ:</strong> {createdClinic.cnpj ? formatCNPJ(createdClinic.cnpj) : '-'}</p>
          <p><strong>Senha temporária:</strong> <code>{createdPassword}</code></p>
          <p className="warning-text">⚠️ Anote esta senha! Ela não será exibida novamente.</p>
          <button onClick={() => { setCreatedPassword(null); setCreatedClinic(null); }} className="btn btn-primary">
            Fechar
          </button>
        </div>
      )}

      {adminCreated && (
        <div className="success-message">
          <h3>Administrador criado com sucesso!</h3>
          <p><strong>Nome:</strong> {adminCreated.name}</p>
          <p><strong>Email:</strong> {adminCreated.email}</p>
          <p>O novo admin pode fazer login com este email e a senha definida.</p>
          <button onClick={() => setAdminCreated(null)} className="btn btn-primary">
            Fechar
          </button>
        </div>
      )}

      {showCreateAdmin && (
        <div className="modal-overlay" onClick={() => setShowCreateAdmin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Criar Novo Administrador</h2>
            <form onSubmit={handleCreateAdmin}>
              <div className="form-group">
                <label htmlFor="admin-email">Email</label>
                <input
                  id="admin-email"
                  type="email"
                  value={adminForm.email}
                  onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                  placeholder="admin@exemplo.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-name">Nome</label>
                <input
                  id="admin-name"
                  type="text"
                  value={adminForm.name}
                  onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                  placeholder="Nome do administrador"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="admin-password">Senha</label>
                <input
                  id="admin-password"
                  type="password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateAdmin(false)} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Criar Administrador
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreateClinic && (
        <div className="modal-overlay" onClick={() => setShowCreateClinic(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Criar Nova Clínica</h2>
            <form onSubmit={handleCreateClinic}>
              <div className="form-group">
                <label htmlFor="cnpj">CNPJ</label>
                <input
                  id="cnpj"
                  type="text"
                  value={clinicForm.cnpj}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 14) {
                      setClinicForm({ ...clinicForm, cnpj: value });
                    }
                  }}
                  placeholder="00.000.000/0000-00"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={clinicForm.email}
                  onChange={(e) => setClinicForm({ ...clinicForm, email: e.target.value })}
                  placeholder="clinica@exemplo.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="name">Nome da Clínica</label>
                <input
                  id="name"
                  type="text"
                  value={clinicForm.name}
                  onChange={(e) => setClinicForm({ ...clinicForm, name: e.target.value })}
                  placeholder="Nome da Clínica"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowCreateClinic(false)} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Criar Clínica
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="users-table-container">
        <h2>Usuários Cadastrados</h2>
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Email</th>
              <th>Tipo</th>
              <th>CPF</th>
              <th>CNPJ</th>
              <th>Data de Criação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map((userItem) => (
              <tr key={userItem.id}>
                <td>{userItem.id}</td>
                <td>{userItem.name}</td>
                <td>{userItem.email}</td>
                <td>
                  <span className={`role-badge role-${userItem.role}`}>
                    {userItem.role === 'super_admin' ? 'Super Admin' :
                     userItem.role === 'admin' ? 'Administrador' :
                     userItem.role === 'clinica' ? 'Clínica' : 'Comum'}
                  </span>
                </td>
                <td>{userItem.cpf ? formatCPF(userItem.cpf) : '-'}</td>
                <td>{userItem.cnpj ? formatCNPJ(userItem.cnpj) : '-'}</td>
                <td>{new Date(userItem.created_at).toLocaleDateString('pt-BR')}</td>
                <td>
                  {userItem.id !== user?.id &&
                    (user?.role === 'super_admin' || userItem.role !== 'super_admin') && (
                    <button
                      onClick={() => handleDeleteUser(userItem.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Deletar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPanel;
